import { useMutation, useQuery } from "@tanstack/react-query";
import { useSkillsRequestContext } from "@/services/skills";
import {
  createPortfolioPass,
  getPortfolioEstimate,
  getPortfolioInventory,
  getPortfolioPassTriage,
  runPortfolioPass,
} from "@/services/migrations/portfolio";
import type {
  PortfolioEstimate,
  PortfolioInventory,
  PortfolioPassCreated,
  PortfolioPassRequest,
  PortfolioPassRunResult,
  PortfolioPassTriage,
} from "@/services/migrations/portfolio";

/**
 * React Query wiring for the portfolio pass.
 *
 * THE FREE READ IS A QUERY; EVERY PAID CALL IS A MUTATION. That split is the point.
 * `/inventory` costs nothing and can be fetched on mount for any org. `/estimate`,
 * `/passes` and `/run` are billable acts and must be reachable only from a deliberate
 * click — a `useQuery` on any of them would fire on mount, on refocus and on remount, and
 * "the component re-rendered" is not consent to spend a customer's money. `/estimate` in
 * particular reads like a GET and is one; it is still a mutation here, because when it
 * fires is a product decision rather than a caching one.
 *
 * NOTHING RETRIES. A 403 is the server's settled answer about entitlement, and retrying
 * it three times turns one refusal into three log lines and a slower screen.
 *
 * ERRORS ARE KEPT, NOT SWALLOWED. The page reads `mutation.error` through
 * `resolvePortfolioGate`, so a 403 from any paid call becomes the same contact path the
 * free inventory would have shown. See `lib/migrations/portfolio.ts`.
 */

/**
 * Statuses after which the pass will not change again. Anything unrecognised KEEPS
 * POLLING: a screen that stops updating on a status nobody anticipated is a screen
 * showing a stale result while looking settled.
 */
const TERMINAL_PASS_STATUSES = new Set(["done", "complete", "completed", "failed", "cancelled", "refused"]);

const TRIAGE_POLL_MS = 5000;

export function portfolioKeys(orgId: string | null) {
  return {
    inventory: ["migration-portfolio", "inventory", orgId] as const,
    triage: (planId: string | undefined) =>
      ["migration-portfolio", "triage", orgId, planId ?? null] as const,
  };
}

/** The free inventory. Fetched for every org, entitled or not — that is what it is for. */
export function usePortfolioInventory() {
  const ctx = useSkillsRequestContext();
  return useQuery<PortfolioInventory>({
    queryKey: portfolioKeys(ctx.orgId).inventory,
    queryFn: getPortfolioInventory,
    enabled: Boolean(ctx.orgId),
    // An org's rule count does not move minute to minute, and this read walks pages of
    // the Metadata API. Long enough to survive a tab switch, short enough that a
    // re-ingested org is not stale all day.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * PAID. Fires on a click, never on a render.
 *
 * DO NOT "FIX" THIS BACK INTO A useQuery. `/estimate` really is a GET, and deliberately
 * so: reading a cost must not be able to start a run, and it creates no plan and spends
 * no deploy. What it DOES do is issue two org listings against the Metadata API, so every
 * automatic refire — mount, refocus, remount, a proxy retry, a router prefetch — burns
 * the customer's Salesforce API quota for a number nobody asked to see. A mutation fires
 * once, when a human asks. The safety here is about quota, not about mutation semantics,
 * which is why the HTTP verb and the hook type disagree on purpose.
 */
export function usePortfolioEstimate() {
  return useMutation<PortfolioEstimate, unknown, void>({
    mutationFn: () => getPortfolioEstimate(),
    retry: false,
  });
}

/** PAID. Only reachable from the confirm step — `accept_estimate` means a human read it. */
export function usePortfolioPassCreate() {
  return useMutation<PortfolioPassCreated, unknown, PortfolioPassRequest>({
    mutationFn: (body) => createPortfolioPass(body),
    retry: false,
  });
}

/** PAID. 202 — the result arrives through the triage query below. */
export function usePortfolioPassRun() {
  return useMutation<PortfolioPassRunResult, unknown, string>({
    mutationFn: (planId) => runPortfolioPass(planId),
    retry: false,
  });
}

/**
 * PAID. The clustered result, POLLED.
 *
 * Polled rather than streamed for the reason the engine's report is: a dropped stream
 * that resolves as "done" would render an unfinished pass as a finished one. The status
 * shown is the status the server last returned.
 */
export function usePortfolioPassTriage(planId: string | undefined) {
  const ctx = useSkillsRequestContext();
  return useQuery<PortfolioPassTriage>({
    queryKey: portfolioKeys(ctx.orgId).triage(planId),
    queryFn: () => getPortfolioPassTriage(planId!),
    enabled: Boolean(planId && ctx.orgId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL_PASS_STATUSES.has(status) ? false : TRIAGE_POLL_MS;
    },
    staleTime: 0,
    retry: false,
  });
}
